/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import {
	catchError,
	combineLatest,
	EMPTY,
	filter,
	map,
	Observable,
	of,
	shareReplay,
	startWith,
	switchMap
} from "rxjs";

export type Loadable<T> = { isLoading: boolean; data?: T; hasError?: boolean };

/**
 * Builds a cold stream that calls `request` only while something subscribes to it.
 * @param filters$ The filters driving the request; `undefined` values are skipped.
 * @param request The API call to perform for each filters value.
 * @param reload$ Optional trigger that repeats the request with the latest filters.
 * @returns A shared stream emitting the loading, data and error state of the request.
 */
export function lazyLoad<F, T>(
	filters$: Observable<F | undefined>,
	request: (filters: F) => Observable<T>,
	reload$: Observable<unknown> = EMPTY
): Observable<Loadable<T>> {
	return combineLatest([
		filters$.pipe(filter((filters): filters is F => filters !== undefined)),
		reload$.pipe(startWith(undefined))
	]).pipe(
		switchMap(([filters]) =>
			request(filters).pipe(
				map((data): Loadable<T> => ({ isLoading: false, data })),
				catchError(() => of<Loadable<T>>({ isLoading: false, hasError: true })),
				startWith<Loadable<T>>({ isLoading: true })
			)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);
}
